/* Unified Inbox - left pane: views, channel filter, search and the triaged thread list. */
import React from "react";
import { Paperclip, Pin, Inbox as InboxIcon, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { relTime } from "@/lib/format";
import { CompanyLogo, UserAvatar, SearchField, Skeleton, EmptyState, InlineError } from "@/components/common";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CHANNELS, CHANNEL_ORDER, PRIORITY, VIEWS, channelMeta, dueLabel } from "./meta";

export function ChannelBadge({ channel, className, size = "sm" }) {
  const m = channelMeta(channel);
  const Icon = m.Icon;
  return (
    <span title={m.label} className={cn("inline-flex shrink-0 items-center justify-center rounded-full text-white ring-2 ring-white", size === "sm" ? "h-[18px] w-[18px]" : "h-6 w-6", m.cls, className)} data-testid={`channel-badge-${channel}`}>
      <Icon className={size === "sm" ? "h-2.5 w-2.5" : "h-3.5 w-3.5"} strokeWidth={2.4} />
    </span>
  );
}

export function PriorityChip({ priority, className, compact = false }) {
  const m = PRIORITY[priority] || PRIORITY.normal;
  if (compact && (priority === "normal" || priority === "low")) return null;
  const Icon = m.Icon;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em]", m.cls, className)} data-testid={`priority-chip-${priority}`}>
      <Icon className="h-2.5 w-2.5" strokeWidth={2.6} /> {m.label}
    </span>
  );
}

function ThreadRow({ t, active, onSelect }) {
  const due = dueLabel(t.ai?.due_at);
  return (
    <button
      type="button"
      onClick={() => onSelect(t.id)}
      data-testid={`inbox-thread-row-${t.id}`}
      data-active={active ? "true" : "false"}
      className={cn(
        "group relative flex w-full gap-3 rounded-xl px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        active ? "bg-white shadow-[0_1px_2px_hsl(30_12%_18%/0.06),0_0_0_1px_hsl(var(--primary)/0.35)]" : "hover:bg-[hsl(var(--muted)/0.6)]"
      )}
    >
      <div className="relative mt-0.5 shrink-0">
        <CompanyLogo name={t.company?.name} domain={t.company?.website} size="md" />
        <ChannelBadge channel={t.channel} className="absolute -bottom-1 -right-1" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn("truncate text-[13px]", t.unread ? "font-semibold text-foreground" : "font-medium text-foreground/85")}>{t.company?.name}</span>
          {t.contact?.name && t.contact?.kind !== "system" ? <span className="truncate text-[12px] text-muted-foreground">· {t.contact.name}</span> : null}
          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            {t.pinned ? <Pin className="h-3 w-3 text-muted-foreground" /> : null}
            <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground" title={t.last_message_at}>{relTime(t.last_message_at)}</span>
            {t.unread ? <span className="h-2 w-2 rounded-full bg-primary" data-testid="inbox-unread-dot" /> : null}
          </span>
        </div>
        <div className={cn("mt-0.5 truncate text-[13.5px] leading-snug", t.unread ? "font-semibold" : "font-medium text-foreground/90")}>{t.subject}</div>
        <div className="mt-0.5 truncate text-[12.5px] leading-snug text-muted-foreground">{t.ai?.summary || t.preview}</div>
        <div className="mt-1.5 flex items-center gap-1.5 overflow-hidden">
          <PriorityChip priority={t.ai?.priority} compact />
          {due ? <span className={cn("rounded-md px-1.5 py-[2px] text-[10.5px] font-medium", due.tone === "bad" ? "bg-[hsl(var(--status-bad)/0.1)] text-[hsl(var(--status-bad))]" : due.tone === "warn" ? "bg-[hsl(var(--status-warn)/0.12)] text-[hsl(35_80%_32%)]" : "bg-muted text-muted-foreground")}>{due.text}</span> : null}
          {(t.labels || []).slice(0, 2).map((l) => (
            <span key={l} className="truncate rounded-md border border-black/[0.07] bg-white px-1.5 py-[2px] text-[10.5px] text-muted-foreground">{l}</span>
          ))}
          {t.has_attachments ? <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" /> : null}
          <span className="ml-auto shrink-0">
            {t.assignee ? <UserAvatar name={t.assignee.name} initials={t.assignee.initials} color={t.assignee.color} src={t.assignee.avatar_url} size="xs" /> : <span className="inline-flex h-5 items-center rounded-full border border-dashed border-black/[0.15] px-1.5 text-[9.5px] text-muted-foreground">Unassigned</span>}
          </span>
        </div>
      </div>
    </button>
  );
}

export function ThreadList({ threads, loading, error, onRetry, activeId, onSelect, view, onView, channel, onChannel, q, onQ, summary, className }) {
  const counts = summary?.counts || {};
  const chCounts = summary?.channels || {};
  return (
    <section className={cn("flex h-full min-h-0 flex-col", className)} data-testid="inbox-list-pane" aria-label="Inbox threads">
      <div className="shrink-0 px-4 pb-3 pt-5">
        <div className="flex items-center gap-2.5">
          <h1 className="text-[22px] font-semibold leading-none tracking-[-0.02em]" data-testid="inbox-title">Inbox</h1>
          {summary?.unread ? <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-white" data-testid="inbox-unread-count">{summary.unread}</span> : null}
          <span className="ml-auto inline-flex items-center gap-1 rounded-md border border-black/[0.07] bg-white px-1.5 py-[2px] text-[9.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground" title="All conversations are demo data">Demo</span>
        </div>
        <p className="mt-1 text-[12.5px] text-muted-foreground">Every conversation across e-mail, Teams, LinkedIn, phone, forms and tender portals – triaged for you.</p>
        <SearchField value={q} onChange={onQ} placeholder="Search people, companies, subjects…" className="mt-3" inputClassName="h-9 text-[13px]" testId="inbox-search-input" />
        <div className="no-scrollbar mt-3 flex items-center gap-0.5 overflow-x-auto rounded-xl border border-black/[0.06] bg-[hsl(40_10%_95%)] p-1" role="tablist">
          {VIEWS.map((v) => {
            const active = v.key === view;
            return (
              <button key={v.key} type="button" role="tab" aria-selected={active} onClick={() => onView(v.key)} data-testid={`inbox-view-${v.key}`} className={cn("inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-[8px] px-2.5 text-[12.5px] font-medium text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40", active && "bg-white text-foreground shadow-[0_1px_2px_hsl(30_12%_18%/0.08),0_0_0_1px_hsl(30_12%_18%/0.04)]")}>
                {v.label}
                {counts[v.key] !== undefined ? <span className={cn("font-mono text-[10.5px] tabular-nums", active ? "text-muted-foreground" : "text-muted-foreground/60")}>{counts[v.key]}</span> : null}
              </button>
            );
          })}
        </div>
        <div className="no-scrollbar mt-2.5 flex items-center gap-1.5 overflow-x-auto" data-testid="inbox-channel-filters">
          <button type="button" onClick={() => onChannel(null)} data-testid="inbox-channel-all" className={cn("inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[12px] font-medium", !channel ? "border-foreground bg-foreground text-white" : "border-black/[0.08] bg-white text-muted-foreground hover:text-foreground")}>
            <SlidersHorizontal className="h-3 w-3" /> All
          </button>
          {CHANNEL_ORDER.map((c) => {
            const m = CHANNELS[c];
            const Icon = m.Icon;
            const active = channel === c;
            return (
              <Tooltip key={c}>
                <TooltipTrigger asChild>
                  <button type="button" onClick={() => onChannel(active ? null : c)} data-testid={`inbox-channel-${c}`} aria-pressed={active} className={cn("inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-2 text-[12px] font-medium", active ? "border-foreground bg-foreground text-white" : "border-black/[0.08] bg-white text-muted-foreground hover:text-foreground")}>
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.1} />
                    <span className="font-mono text-[10.5px] tabular-nums">{chCounts[c] ?? 0}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{m.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
      <div className="scroll-fade-b min-h-0 flex-1 overflow-y-auto px-2 pb-4 scrollbar-thin" data-testid="inbox-thread-list">
        {error ? <div className="px-2 pt-2"><InlineError onRetry={onRetry} testId="inbox-list-error">{error}</InlineError></div> : null}
        {loading && !threads ? (
          <div className="space-y-2 px-2 pt-2">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex gap-3 rounded-xl p-3"><Skeleton className="h-10 w-10 rounded-xl" /><div className="flex-1 space-y-2"><Skeleton className="h-3 w-1/2" /><Skeleton className="h-3 w-5/6" /><Skeleton className="h-3 w-2/3" /></div></div>
            ))}
          </div>
        ) : null}
        {threads && threads.length === 0 && !error ? (
          <EmptyState icon={InboxIcon} title="Nothing here" description={q ? "No conversations match your search." : "You're all caught up in this view."} className="mx-2 mt-4" testId="inbox-empty" />
        ) : null}
        <div className="space-y-1 pt-1">
          {(threads || []).map((t) => (
            <ThreadRow key={t.id} t={t} active={t.id === activeId} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </section>
  );
}
